import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Mail, Lock, ShieldCheck, User as UserIcon, ArrowLeft } from "lucide-react";
import { apiClient } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import { SettingsTwoFactorSection } from "@/components/SettingsTwoFactorSection";
import TrustedDevicesCard from "@/components/TrustedDevicesCard";
import TrusteeDigestSettingsCard from "@/components/TrusteeDigestSettingsCard";
import ActiveSessionsCard from "@/components/ActiveSessionsCard";
import LoginHistoryCard from "@/components/LoginHistoryCard";

export default function Account() {
  const navigate = useNavigate();
  const { setEmail: setAuthEmail } = useAuth();

  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(true);

  // Email form
  const [newEmail, setNewEmail] = useState("");
  const [currentPwForEmail, setCurrentPwForEmail] = useState("");
  const [emailBusy, setEmailBusy] = useState(false);

  // Password form
  const [currentPw, setCurrentPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [newPwConfirm, setNewPwConfirm] = useState("");
  const [pwBusy, setPwBusy] = useState(false);

  useEffect(() => {
    let active = true;
    apiClient
      .get("/auth/me")
      .then((res) => {
        if (active) setMe(res.data);
      })
      .catch(() => {
        if (active) toast.error("Profil konnte nicht geladen werden");
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, []);

  const handleChangeEmail = async (e) => {
    e.preventDefault();
    if (!newEmail || !currentPwForEmail) {
      toast.error("Bitte neue E-Mail und aktuelles Passwort eingeben");
      return;
    }
    setEmailBusy(true);
    try {
      const res = await apiClient.put("/auth/me/email", {
        new_email: newEmail,
        current_password: currentPwForEmail,
      });
      const updatedEmail = res.data?.email || newEmail;
      toast.success(`E-Mail geändert auf ${updatedEmail}`);
      setMe((m) => (m ? { ...m, email: updatedEmail } : m));
      setAuthEmail(updatedEmail);
      localStorage.setItem("authEmail", updatedEmail);
      setNewEmail("");
      setCurrentPwForEmail("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Änderung fehlgeschlagen");
    } finally {
      setEmailBusy(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (!currentPw || !newPw) {
      toast.error("Bitte aktuelles und neues Passwort eingeben");
      return;
    }
    if (newPw.length < 8) {
      toast.error("Neues Passwort muss mindestens 8 Zeichen lang sein");
      return;
    }
    if (newPw !== newPwConfirm) {
      toast.error("Passwort-Bestätigung stimmt nicht überein");
      return;
    }
    setPwBusy(true);
    try {
      await apiClient.put("/auth/me/password", {
        current_password: currentPw,
        new_password: newPw,
      });
      toast.success("Passwort wurde geändert");
      setCurrentPw("");
      setNewPw("");
      setNewPwConfirm("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Änderung fehlgeschlagen");
    } finally {
      setPwBusy(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto space-y-6" data-testid="account-page">
      {typeof window !== "undefined" && window.location.search.includes("reason=2fa-required") && (
        <div className="rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900" data-testid="account-2fa-required-banner">
          <strong>2FA erforderlich:</strong> {sessionStorage.getItem("tfaRequiredMessage") || "Diese Firma verlangt Zwei-Faktor-Authentifizierung."} Richten Sie 2FA unten ein, danach haben Sie wieder Zugriff.
        </div>
      )}
      <div className="flex items-center gap-3">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate(-1)}
          className="text-slate-600"
          data-testid="account-back-button"
        >
          <ArrowLeft className="h-4 w-4 mr-1.5" />
          Zurück
        </Button>
      </div>

      <div>
        <h1 className="text-3xl font-semibold tracking-tight text-slate-900">Mein Account</h1>
        <p className="mt-1 text-sm text-slate-500">
          Verwalte deine Login-Daten. Änderungen wirken sich sofort auf deinen nächsten Login aus.
        </p>
      </div>

      {/* Profile Card */}
      <Card className="p-5 border border-slate-200 bg-white shadow-sm" data-testid="account-profile-card">
        <div className="flex items-center gap-4">
          <div className="h-14 w-14 rounded-full bg-indigo-100 text-indigo-700 flex items-center justify-center">
            <UserIcon className="h-7 w-7" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-base font-semibold text-slate-900 truncate" data-testid="account-email">
              {loading ? "Lädt …" : me?.email}
            </div>
            <div className="text-xs text-slate-500 mt-0.5 flex items-center gap-2">
              {me?.is_super_admin && (
                <span
                  className="inline-flex items-center gap-1 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 px-2 py-0.5 text-[11px] font-medium"
                  data-testid="account-super-admin-badge"
                >
                  <ShieldCheck className="h-3 w-3" /> Super-Admin
                </span>
              )}
              {me?.name && <span>{me.name}</span>}
            </div>
          </div>
        </div>
      </Card>

      {/* Change Email */}
      <Card className="p-5 border border-slate-200 bg-white shadow-sm" data-testid="account-email-card">
        <div className="flex items-center gap-2 mb-4">
          <Mail className="h-5 w-5 text-slate-600" />
          <h2 className="text-lg font-semibold text-slate-900">E-Mail-Adresse ändern</h2>
        </div>
        <form onSubmit={handleChangeEmail} className="space-y-4" data-testid="account-email-form">
          <div className="space-y-1.5">
            <Label htmlFor="new-email">Neue E-Mail-Adresse</Label>
            <Input
              id="new-email"
              type="email"
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              placeholder="neue@email.ch"
              data-testid="account-new-email-input"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="current-pw-email">Aktuelles Passwort zur Bestätigung</Label>
            <Input
              id="current-pw-email"
              type="password"
              value={currentPwForEmail}
              onChange={(e) => setCurrentPwForEmail(e.target.value)}
              autoComplete="current-password"
              data-testid="account-current-password-for-email-input"
            />
          </div>
          <Button
            type="submit"
            disabled={emailBusy}
            className="bg-slate-900 hover:bg-slate-800 text-white"
            data-testid="account-submit-email-button"
          >
            {emailBusy ? "Speichern …" : "E-Mail ändern"}
          </Button>
        </form>
      </Card>

      {/* Change Password */}
      <Card className="p-5 border border-slate-200 bg-white shadow-sm" data-testid="account-password-card">
        <div className="flex items-center gap-2 mb-4">
          <Lock className="h-5 w-5 text-slate-600" />
          <h2 className="text-lg font-semibold text-slate-900">Passwort ändern</h2>
        </div>
        <form onSubmit={handleChangePassword} className="space-y-4" data-testid="account-password-form">
          <div className="space-y-1.5">
            <Label htmlFor="current-pw">Aktuelles Passwort</Label>
            <Input
              id="current-pw"
              type="password"
              value={currentPw}
              onChange={(e) => setCurrentPw(e.target.value)}
              autoComplete="current-password"
              data-testid="account-current-password-input"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="new-pw">Neues Passwort (mind. 8 Zeichen)</Label>
            <Input
              id="new-pw"
              type="password"
              value={newPw}
              onChange={(e) => setNewPw(e.target.value)}
              autoComplete="new-password"
              data-testid="account-new-password-input"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="new-pw-confirm">Neues Passwort bestätigen</Label>
            <Input
              id="new-pw-confirm"
              type="password"
              value={newPwConfirm}
              onChange={(e) => setNewPwConfirm(e.target.value)}
              autoComplete="new-password"
              data-testid="account-new-password-confirm-input"
            />
          </div>
          <Button
            type="submit"
            disabled={pwBusy}
            className="bg-slate-900 hover:bg-slate-800 text-white"
            data-testid="account-submit-password-button"
          >
            {pwBusy ? "Speichern …" : "Passwort ändern"}
          </Button>
        </form>
      </Card>

      {/* 2FA Section */}
      <div data-testid="account-2fa-wrapper">
        <SettingsTwoFactorSection />
      </div>

      {/* Aktive Anmeldungen (Cookie-Sessions) */}
      <ActiveSessionsCard />

      {/* Anmelde-Verlauf */}
      <LoginHistoryCard />

      {/* Trusted Devices */}
      <TrustedDevicesCard />
      <TrusteeDigestSettingsCard />
    </div>
  );
}
